import { resourceId } from "./tutorWire";

interface LogoutCoordination {
  run<Value>(ownerId: string, work: () => Promise<Value>, signal?: AbortSignal): Promise<Value>;
  queued(ownerId: string, marker: string): void;
  completed(ownerId: string, marker: string): void;
  ready(ownerId: string): void;
}

/** App-owned cleanup outlives the Tutor route. A later login for the same owner
 * must await it so delayed cleanup cannot erase that login's new attempts. */
export function createTutorVoiceLogoutCleanup(purge: (ownerId: string) => Promise<void>, coordination?: LogoutCoordination) {
  const pending = new Map<string, Promise<void>>();
  function run<Value>(ownerId: string, work: () => Promise<Value>, signal?: AbortSignal): Promise<Value> {
    resourceId(ownerId);
    // Capture before entering the lock: waiting on a later queued purge while
    // holding this lock would prevent that purge from ever running.
    const previous = pending.get(ownerId) ?? Promise.resolve();
    const execute = async () => { await previous; signal?.throwIfAborted(); coordination?.ready(ownerId); return work(); };
    try { return coordination ? coordination.run(ownerId, execute, signal) : execute(); }
    catch (error) { return Promise.reject(error); }
  }
  return {
    queue(ownerId: string, cleanupOwner = purge) {
      resourceId(ownerId);
      const previous = pending.get(ownerId) ?? Promise.resolve();
      let cleanup: Promise<void>;
      try {
        const marker = coordination ? crypto.randomUUID() : "";
        // Announce and enqueue the cross-tab lock synchronously, before any
        // dynamic import. Failed/abandoned cleanup leaves a durable barrier.
        coordination?.queued(ownerId, marker);
        const execute = async () => {
          await previous.catch(() => undefined); await cleanupOwner(ownerId);
          coordination?.completed(ownerId, marker);
        };
        cleanup = coordination ? coordination.run(ownerId, execute) : execute();
      } catch (error) { cleanup = Promise.reject(error); }
      pending.set(ownerId, cleanup);
      void cleanup.then(() => { if (pending.get(ownerId) === cleanup) pending.delete(ownerId); }, () => undefined);
      return cleanup;
    },
    run,
    wait(ownerId: string) { return run(ownerId, async () => {}); },
  };
}

const markerKey = (ownerId: string) => `jungle-tutor-voice-cleanup-v1:${ownerId}`;
export const tutorVoiceLogoutCleanup = createTutorVoiceLogoutCleanup(async ownerId => {
  // Only an actual account transition loads storage; ordinary startup and
  // token refresh do not pull the voice client into the startup bundle.
  const { createIndexedDbTutorVoiceJournal } = await import("./tutorVoiceJournal");
  const journal = createIndexedDbTutorVoiceJournal();
  try { await journal.purge(ownerId); } finally { journal.close(); }
}, {
  run(ownerId, work, signal) {
    const locks = globalThis.navigator?.locks;
    if (!locks) throw new Error("tutor_voice_cleanup_unavailable");
    return locks.request(markerKey(ownerId), { signal }, work);
  },
  queued(ownerId, marker) { localStorage.setItem(markerKey(ownerId), marker); },
  completed(ownerId, marker) {
    // A later queued purge owns its own barrier. An earlier completion cannot
    // clear it, even when another tab has already announced that cleanup.
    if (localStorage.getItem(markerKey(ownerId)) === marker) localStorage.removeItem(markerKey(ownerId));
  },
  ready(ownerId) {
    if (localStorage.getItem(markerKey(ownerId)) !== null) throw new Error("tutor_voice_cleanup_unavailable");
  },
});
